// src/pages/News.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Typography,
    Card,
    Stack,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate } from 'react-router-dom';

import { useNewsPosts, parseYMD, formatDate, monthLabel } from '../../hooks/useNewPosts';

const firstParagraph = (article) => article.summary || '';

export default function News() {
    const navigate = useNavigate();
    const { articles, loading, error } = useNewsPosts(50); // latest 50 is plenty

    // Build availability maps: { year -> Set(months) }
    const yearToMonths = useMemo(() => {
        const map = new Map();
        for (const a of articles) {
            const d = parseYMD(a.date);
            const y = d.getFullYear();
            const m = d.getMonth() + 1;
            if (!map.has(y)) map.set(y, new Set());
            map.get(y).add(m);
        }
        return map;
    }, [articles]);

    const yearOptions = useMemo(
        () => Array.from(yearToMonths.keys()).sort((a, b) => b - a),
        [yearToMonths]
    );

    const [year, setYear] = useState('all'); // 'all' or specific year
    const [month, setMonth] = useState(0); // 0 = All months, else 1-12

    const monthSetForUI = useMemo(() => {
        if (year === 'all') {
            const union = new Set();
            for (const s of yearToMonths.values()) for (const m of s) union.add(m);
            return union;
        }
        return yearToMonths.get(Number(year)) ?? new Set();
    }, [year, yearToMonths]);

    const monthOptions = useMemo(
        () => Array.from(monthSetForUI).sort((a, b) => b - a),
        [monthSetForUI]
    );

    useEffect(() => {
        if (month !== 0 && !monthSetForUI.has(month)) setMonth(0);
    }, [month, monthSetForUI]);

    const filtered = useMemo(
        () =>
            articles.filter((a) => {
                const d = parseYMD(a.date);
                const yOk = year === 'all' ? true : d.getFullYear() === Number(year);
                const mOk = month === 0 ? true : d.getMonth() + 1 === Number(month);
                return yOk && mOk;
            }),
        [articles, year, month]
    );

    const MAX_VISIBLE = 3;
    const visibleArticles = filtered.slice(0, MAX_VISIBLE);
    const hasMore = filtered.length > MAX_VISIBLE;

    // Popup state (with slideshow index)
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(null);
    const [imgIndex, setImgIndex] = useState(0);

    const openArticle = (article) => {
        setActive(article);
        setOpen(true);
        setImgIndex(0);
    };

    const close = () => {
        setOpen(false);
        setActive(null);
        setImgIndex(0);
    };

    const handleViewMoreNews = () => {
        navigate('/news');
    };

    let content;
    if (loading) {
        content = (
            <Typography variant="body2" color="text.secondary">
                Loading news…
            </Typography>
        );
    } else if (error) {
        content = (
            <Typography variant="body2" color="error">
                {error}
            </Typography>
        );
    } else if (!filtered.length) {
        content = (
            <Typography variant="body2" color="text.secondary">
                No news posts match your filters yet. Please try a different year or
                month, or check back soon.
            </Typography>
        );
    } else {
        content = (
            <Card
                elevation={2}
                sx={{
                    borderRadius: 2,
                    overflow: 'hidden',
                }}
            >
                {visibleArticles.map((a, idx) => (
                    <Box
                        key={a.id}
                        onClick={() => openArticle(a)}
                        sx={(theme) => ({
                            display: 'flex',
                            gap: 2,
                            alignItems: 'flex-start',
                            px: 2,
                            py: 1.5,
                            cursor: 'pointer',
                            transition: 'background-color 0.15s ease',
                            '&:hover': {
                                backgroundColor: theme.palette.action.hover,
                            },
                            borderBottom:
                                idx < visibleArticles.length - 1
                                    ? `1px solid ${theme.palette.divider}`
                                    : 'none',
                        })}
                    >
                        {a.coverUrl && (
                            <Box
                                component="img"
                                src={a.coverUrl}
                                alt={a.title}
                                sx={{
                                    width: 110,
                                    height: 80,
                                    borderRadius: 1,
                                    objectFit: 'cover',
                                    flexShrink: 0,
                                }}
                            />
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontWeight: 600,
                                    mb: 0.25,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {a.title}
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', mb: 0.5 }}
                            >
                                {formatDate(a.date)}
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                }}
                            >
                                {firstParagraph(a)}
                            </Typography>
                        </Box>
                    </Box>
                ))}

                {hasMore && (
                    <Box
                        sx={(theme) => ({
                            borderTop: `1px solid ${theme.palette.divider}`,
                            px: 2,
                            py: 1,
                            textAlign: 'center',
                        })}
                    >
                        <Button
                            size="small"
                            onClick={handleViewMoreNews}
                            sx={{ fontWeight: 600 }}
                        >
                            View More News
                        </Button>
                    </Box>
                )}
            </Card>
        );
    }

    const activeText = (active?.body || active?.summary || '').split(/\n{2,}/);
    const activeParagraphs = activeText
        .map((s) => s.trim())
        .filter(Boolean);

    return (
        <Box>
            <Typography
                variant="h4"
                sx={{
                    fontWeight: 700,
                    mb: 2,
                    fontSize: { xs: '1.6rem', sm: '1.8rem' },
                    lineHeight: { xs: 1.25, sm: 1.25 },
                }}
            >
                Latest News &amp; Updates
            </Typography>

            {/* Filters */}
            <Stack
                direction="row"
                spacing={2}
                sx={{ mb: 2 }}
                alignItems="center"
                flexWrap="wrap"
            >
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel id="news-year-label">Year</InputLabel>
                    <Select
                        labelId="news-year-label"
                        label="Year"
                        value={year}
                        onChange={(e) => setYear(e.target.value)}
                    >
                        <MenuItem value="all">All years</MenuItem>
                        {yearOptions.map((y) => (
                            <MenuItem key={y} value={y}>
                                {y}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel id="news-month-label">Month</InputLabel>
                    <Select
                        labelId="news-month-label"
                        label="Month"
                        value={month}
                        onChange={(e) => setMonth(e.target.value)}
                    >
                        <MenuItem value={0}>All months</MenuItem>
                        {monthOptions.map((m) => (
                            <MenuItem key={m} value={m}>
                                {monthLabel(m)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Stack>

            {content}

            {/* Popup for full story + slideshow */}
            <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
                <DialogTitle sx={{ pr: 6 }}>
                    {active?.title}
                    <IconButton
                        onClick={close}
                        aria-label="Close"
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            bgcolor: 'rgba(0,0,0,0.65)',
                            color: '#fff',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.85)' },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ position: 'relative' }}>
                    {active && (
                        <Stack spacing={2}>
                            <Box
                                sx={{
                                    position: 'relative',
                                    width: '100%',
                                    maxHeight: { xs: '52vh', md: '60vh' },
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    bgcolor: 'common.black',
                                }}
                            >
                                {Array.isArray(active.images) &&
                                    active.images.length > 1 && (
                                        <>
                                            <IconButton
                                                aria-label="Previous photo"
                                                onClick={() =>
                                                    setImgIndex(
                                                        (i) =>
                                                            (i -
                                                                1 +
                                                                active.images
                                                                    .length) %
                                                            active.images.length
                                                    )
                                                }
                                                sx={{
                                                    position: 'absolute',
                                                    left: 8,
                                                    top: '50%',
                                                    transform:
                                                        'translateY(-50%)',
                                                    bgcolor: 'rgba(0,0,0,0.5)',
                                                    color: '#fff',
                                                    '&:hover': {
                                                        bgcolor:
                                                            'rgba(0,0,0,0.7)',
                                                    },
                                                }}
                                            >
                                                <ChevronLeftIcon />
                                            </IconButton>
                                            <IconButton
                                                aria-label="Next photo"
                                                onClick={() =>
                                                    setImgIndex(
                                                        (i) =>
                                                            (i + 1) %
                                                            active.images.length
                                                    )
                                                }
                                                sx={{
                                                    position: 'absolute',
                                                    right: 8,
                                                    top: '50%',
                                                    transform:
                                                        'translateY(-50%)',
                                                    bgcolor: 'rgba(0,0,0,0.5)',
                                                    color: '#fff',
                                                    '&:hover': {
                                                        bgcolor:
                                                            'rgba(0,0,0,0.7)',
                                                    },
                                                }}
                                            >
                                                <ChevronRightIcon />
                                            </IconButton>
                                        </>
                                    )}

                                {active.images && active.images.length > 0 && (
                                    <Box
                                        component="img"
                                        src={active.images[imgIndex]}
                                        alt={active.title}
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'contain',
                                        }}
                                    />
                                )}
                            </Box>

                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 700 }}
                                >
                                    {formatDate(active.date)}
                                </Typography>
                                {activeParagraphs.map((p, i) => (
                                    <Typography
                                        key={i}
                                        variant="body2"
                                        sx={{ mt: i === 0 ? 1 : 1.25 }}
                                    >
                                        {p}
                                    </Typography>
                                ))}
                                {Array.isArray(active.images) &&
                                    active.images.length > 1 && (
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ display: 'block', mt: 1 }}
                                        >
                                            Photo {imgIndex + 1} of{' '}
                                            {active.images.length}
                                        </Typography>
                                    )}
                            </Box>
                        </Stack>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
}
